import Grid from '@mui/material/Grid';
import is from '@sindresorhus/is';
import { format } from 'date-fns';
import { Fragment, useContext } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '../../../../../../../shared/source/context/context';
import { StyledMiddleDot } from '../../../../../design/components/StyledMiddleDot';
import { SmallText } from '../../../../../design/components/Typography';

import type { FunctionComponent } from 'react';

const getDateMaybe = (dateString: string | undefined): Maybe<Date> => {
    if (!is.nonEmptyString(dateString)) {
        return Maybe.nothing();
    }

    try {
        return Maybe.of(new Date(dateString));
    } catch {
        return Maybe.nothing();
    }
};

const useDuration = (
    dateFrom?: string | undefined,
    dateTo?: string | undefined,
    dateFormat?: string | undefined,
): string => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { language } = applicationState;

    const validDateFormat = dateFormat ?? 'MMMM yyyy';

    const formatDate = (date: Date): string => format(date, validDateFormat, { locale: language.date.locale });

    const parsedDateFromMaybe = getDateMaybe(dateFrom);
    const formattedDateFrom = parsedDateFromMaybe.isJust ? formatDate(parsedDateFromMaybe.value) : null;

    const parsedDateToMaybe = getDateMaybe(dateTo);

    const formattedDateTo = parsedDateToMaybe.isJust
        ? formatDate(parsedDateToMaybe.value)
        : translate('pages.recruiter-profile.background.present');

    return [formattedDateFrom, formattedDateTo].filter(is.nonEmptyString).join(' – ');
};

type HistoryItemInformationProps = {
    readonly dateFormat?: string | undefined;
    readonly dateFrom?: string | undefined;
    readonly dateTo?: string | undefined;
    readonly information?: ReadonlyArray<string | undefined>;
};

export const HistoryItemInformation: FunctionComponent<HistoryItemInformationProps> = (props) => {
    const { dateFormat, dateFrom, dateTo, information } = props;
    const formattedDuration = useDuration(dateFrom, dateTo, dateFormat);

    const content = [formattedDuration, ...(information ?? [])].filter(Boolean);

    return (
        <Grid alignItems="center" container={true} gap={2}>
            {content.map((element, index) => (
                <Fragment key={element}>
                    <SmallText>{element}</SmallText>
                    {index !== content.length - 1 && <StyledMiddleDot />}
                </Fragment>
            ))}
        </Grid>
    );
};
